import _ from "lodash";
import { connect } from "react-redux";
import { deviceDetect } from "react-device-detect";
import LogRocket from "logrocket";
import moment from "moment-timezone";
import React, { Component, createRef } from "react";
import video, { Logger } from "twilio-video";

import { EXTENSIBLE_TOAST_ERROR } from "@/components/UI/Alert";
import { getCPTWorkflow } from "@/features/cpt-workflow/workflow-schema";
import { getQueryParams } from "@/utils/filters";
import { isAuthorized } from "@/utils/authToken";
import { resetSessionTimeout } from "@/redux/actions/logout";
import { sharedRequests } from "@/api/(requests)/shared";
import { showAlertWithAction, showAlertWithCustomHTML } from "@/redux/actions/alerts";
import Spinner from "@/components/UI/Spinner";

import { CallStepRouter } from "./components/CallStepRouter";
import { generateVideoCallErrorLog } from "./helpers";
import { UNSTABLE_CONNECT_ERROR_MESSAGE } from "./constants";

class VideoClient extends Component {
  localVideoRef = createRef();
  localAudioRef = createRef();
  remoteVideoRef = createRef();
  remoteAudioRef = createRef();
  remoteVideoPlaceholderRef = createRef();

  /* oxlint-disable jsx-a11y/media-has-caption */
  localMedia = (
    <>
      <video autoPlay ref={this.localVideoRef} />
      <audio autoPlay ref={this.localAudioRef} />
    </>
  );

  remoteMedia = (
    <>
      <video autoPlay ref={this.remoteVideoRef} />
      <audio autoPlay ref={this.remoteAudioRef} />
    </>
  );
  /* oxlint-enable jsx-a11y/media-has-caption */

  state = {
    activeRoom: null,
    audioOff: false,
    callDetails: null,
    callDuration: 0,
    isConnected: false,
    isConnecting: false,
    localTracks: [],
    otherParticipantName: null,
    remoteTracks: [],
    roomName: null,
    startTouchPosition: "",
    step: "ALLOW_AV",
    videoOff: false,
  };

  async componentDidMount() {
    /**
     * fetch the callId and otherParticipantName from url
     * URL example: `www.domain.com/video-call?callId=xxx&userName=userName=yyyy`
     */
    let callId = getQueryParams("callId", this.props.match.search);
    let tokenResponse;

    try {
      tokenResponse = await this.fetchTwilioToken({ showLoader: true });
    } catch {
      this.props.history.push("/app/home");
    }

    if (!tokenResponse) return;

    let callDetails = tokenResponse.call_details;
    let startTime = moment.utc(callDetails.timestamp);
    let allottedTime = tokenResponse.call_details.allotted_time;
    let warningTime = moment.utc(callDetails.timestamp).add(allottedTime - 5, "minutes");
    let endTime = moment.utc(callDetails.timestamp).add(allottedTime, "minutes");

    let workflow;
    let otherParticipantName;

    if (!isAuthorized("user:patient")) {
      // this is where the workflow configs are pulled in and set in state
      // based on the calls cpt code
      workflow = _.cloneDeep(getCPTWorkflow(callDetails.event_type, this.props.profile));
      otherParticipantName = callDetails.patient_name;
    } else {
      otherParticipantName = callDetails.provider_name;
    }

    this.resetSessionInterval = setInterval(resetSessionTimeout, 5 * 1000 * 60);

    this.setState({
      allottedTime,
      callDetails,
      endTime,
      otherParticipantName,
      roomName: callId,
      startTime,
      token: tokenResponse.access_token,
      warningTime,
      workflow,
    });

    this.setLoggers(callId, this.props.showAlertWithAction);
  }

  setLoggers(callId, alertFunction) {
    let logger = Logger.getLogger("twilio-video");
    const originalFactory = logger.methodFactory;

    logger.methodFactory = function (methodName, level, loggerName) {
      const loggerMethod = originalFactory(methodName, level, loggerName);

      return (datetime, logLevel, component, message, metadata) => {
        loggerMethod(datetime, logLevel, component, message, metadata);

        if (message === "event" && metadata.group === "signaling") {
          if (metadata.name === "closed" && metadata.level === "error") {
            let data = generateVideoCallErrorLog(
              callId,
              message,
              "VideoClient.twilioErrorLogging",
              metadata,
            );

            sharedRequests.report_video_call_error({ data });
            alertFunction(UNSTABLE_CONNECT_ERROR_MESSAGE);
          }
        }
      };
    };

    logger.setLevel("error");
  }

  async fetchTwilioToken({ showLoader = false } = {}) {
    let callId = getQueryParams("callId", this.props.match.search);

    if (!callId) {
      this.props.showAlertWithAction("Invalid Request. Missing required field: callId.");
      this.props.history.push("/app/home");

      return;
    }

    let data = {
      call_id: callId,
      device: {
        device_info: deviceDetect(),
        screen: {
          devicePixelRatio: Math.round(window.devicePixelRatio * 100),
          height: window.screen.height,
          width: window.screen.width,
        },
      },
    };

    return sharedRequests.fetch_twilio_token({ context: { showLoader }, data });
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.profile && this.props.profile) {
      let callId = getQueryParams("callId", this.props.match.search);

      if (!callId) {
        this.props.showAlertWithAction("Invalid Request. Missing required field: callId.");
        this.props.history.push("/app/home");
      }
    }
  }

  componentDidCatch(error, errorInfo) {
    let data = generateVideoCallErrorLog(
      this.state.callDetails.callId,
      error,
      "VideoClient.componentDidCatch",
      errorInfo,
    );

    sharedRequests.report_video_call_error({ data });
  }

  componentWillUnmount() {
    /**
     * Clean up after component unmounts
     */
    if (this.state.activeRoom) {
      this.endCall();
    }

    if (this.state.localTracks.length > 0) {
      this.detachTracks(this.state.localTracks);
    }

    if (this.resetSessionInterval) {
      clearInterval(this.resetSessionInterval);
    }
  }

  startPreview = async () => {
    try {
      const localTracks = await video.createLocalTracks();

      this.setState({ localTracks, step: "PREVIEW" });

      this.attachTracks(localTracks, this.localVideoRef, this.localAudioRef);
    } catch (e) {
      console.log(e);
      await this.attachError(e);
    }
  };

  attachLocalTracks = () => {
    if (this.state.localTracks.length > 0) {
      this.attachTracks(this.state.localTracks, this.localVideoRef, this.localAudioRef);
    }
  };

  attachRemoteTracks = () => {
    if (this.state.remoteTracks.length > 0) {
      this.attachTracks(this.state.remoteTracks, this.remoteVideoRef, this.remoteAudioRef);
    }
  };

  setStep = (step) => {
    this.setState({ step });
  };

  startQuestions = () => {
    this.setState({ step: "QUESTIONS" });
  };

  startCall = async () => {
    // Guard: Prevent duplicate connection attempts
    if (this.state.isConnecting || this.state.isConnected) {
      console.warn("VideoCall: Connection already in progress or established, ignoring duplicate startCall");
      return;
    }

    if (!this.state.roomName.trim()) {
      this.props.showAlertWithAction("No room found");

      return;
    }

    this.setState({ isConnecting: true });

    let connectOptions = {
      name: this.state.roomName,
    };

    if (this.state.localTracks) {
      connectOptions.tracks = this.state.localTracks;
    }

    video.connect(this.state.token, connectOptions).then(this.roomJoined, this.attachError);
  };

  attachError = async (error) => {
    // Clear isConnecting immediately - prevents stuck button on all error paths
    // Token refresh will set it back to true when it retries
    this.setState({ isConnecting: false });

    let message;

    switch (error.name) {
      case "NotReadableError":
        message =
          "Close any other applications using your camera or microphone. Close other browser tabs or restart your browser, then rejoin the call.";
        break;

      case "NotFoundError":
        message =
          "Ensure your camera and microphone are both connected. Verify you have given aptihealth permission to access these devices, then rejoin the call.";
        break;

      case "OverconstrainedError":
        message = "Ensure your camera and microphone are still connected, then rejoin the call.";
        break;

      case "NotAllowedError":
        message =
          "You have denied permission to let aptihealth use your microphone and camera. " +
          "The video call will not be able to proceed. Please contact support at support@aptihealth.com if you need help enabling permissions.";
        break;

      case "TwilioError":
        switch (error.code) {
          case 20104:
            if (!this.state.tokenErrorRefreshAttempted) {
              let tokenResponse = await this.fetchTwilioToken();

              if (!tokenResponse) {
                return;
              }
              this.setState(
                {
                  token: tokenResponse.access_token,
                  tokenErrorRefreshAttempted: true,
                },
                this.startCall,
              );
            } else {
              let data = generateVideoCallErrorLog(
                this.state.callDetails.callId,
                error,
                "VideoClient.attachError",
              );

              sharedRequests.report_video_call_error({ data });
            }
            break;

          case 53000:
            message = UNSTABLE_CONNECT_ERROR_MESSAGE;
            break;

          case 53205:
            message =
              "You may have joined this call from multiple browsers or browser tabs. Close out all other browsers and browser tabs, then rejoin the call.";
            break;

          case 53404:
            message =
              "Your browser or browser version may not be supported. Try another browser, or update your current browser, then rejoin the call.";
            break;

          default:
            break;
        }
        break;

      case "AbortError":
        message =
          "We're setting up your video. Please wait a few more seconds. If nothing happens, try refreshing the page.";
        LogRocket.captureMessage(error.toString());
        break;

      default:
        sharedRequests.report_video_call_error({
          data: generateVideoCallErrorLog(
            this.state.callDetails.callId,
            error,
            "VideoClient.attachError",
          ),
        });

        if (error.message === "getUserMedia is not supported") {
          message =
            "You are using an unsupported OS and/or Browser. " +
            "If you are using an iOS device try switching to Safari. " +
            "Please contact support at support@aptihealth.com if you continue to have issues.";
        }
        break;
    }

    if (message) {
      this.props.showAlertWithAction(message);
    } else {
      const unknownErrorMessage = (
        <div>
          <p>An error occurred while starting the call. Please try the following:</p>
          <ul>
            <li> - Close other apps or tabs using the camera.</li>
            <li> - Restart your browser.</li>
            <li> - Try a different browser.</li>
            <li> - Check your camera permissions.</li>
            <li> - Ensure your camera is connected.</li>
          </ul>
          <p>
            If the issue persists, contact support at{" "}
            <a
              href="mailto:support@aptihealth.com"
              style={{ color: "white", textDecoration: "underline" }}
            >
              support@aptihealth.com
            </a>
            .
          </p>
        </div>
      );

      this.props.showAlertWithCustomHTML(
        unknownErrorMessage,
        EXTENSIBLE_TOAST_ERROR,
        false,
        "SnackBar__GenericVideoError",
      );
    }
  };

  roomJoined = async (room) => {
    room.on("participantConnected", this.participantConnected);
    room.on("participantDisconnected", this.participantDisconnected);
    room.on("disconnected", () => {
      this.detachTracks(this.state.localTracks);
      this.setState({ isConnected: false, isConnecting: false });
    });
    room.participants.forEach(this.participantConnected);

    // Set activeRoom immediately so endCall/unmount can disconnect properly
    this.setState({
      activeRoom: room,
      isConnected: true,
      isConnecting: false,
      step: "IN_ROOM",
    });

    // Fetch updated call details (non-critical, fallback to existing if fails)
    try {
      let tokenResponse = await this.fetchTwilioToken();
      
      if (tokenResponse) {
        let callDetails = tokenResponse.call_details;
        this.setState({ callDetails });
      }
    } catch (error) {
      // Token fetch failed but we're already connected - continue with existing callDetails
      console.warn("Failed to fetch updated call details, using existing:", error);
    }

    this.attachLocalTracks();
    this.attachRemoteTracks();
  };

  participantConnected = async (participant) => {
    participant.on("trackPublished", this.trackPublished);
    participant.on("trackSubscriptionFailed", this.trackSubscriptionFailed);
    participant.tracks.forEach(this.trackPublished);
    if (!isAuthorized("user:patient")) {
      let tokenResponse = await this.fetchTwilioToken();

      if (!tokenResponse) {
        return;
      }
      let callDetails = tokenResponse.call_details;

      this.setState({ callDetails });
    }
  };

  trackPublished = async (publication) => {
    publication.on("subscribed", this.trackSubscribed);
  };

  trackSubscribed = async (track) => {
    let remoteTracks = this.state.remoteTracks;

    remoteTracks.push(track);
    this.setState({ remoteTracks });
    this.attachTracks(remoteTracks, this.remoteVideoRef, this.remoteAudioRef);
  };

  trackSubscriptionFailed = async (error) => {
    let data = generateVideoCallErrorLog(
      this.state.callDetails.callId,
      error,
      "VideoClient.trackSubscriptionFailed",
    );

    sharedRequests.report_video_call_error({ data });
  };

  participantDisconnected = () => {
    this.setState({
      participantDisconnected: true,
      remoteTracks: [],
    });
  };

  endCall = (route = true) => {
    this.state.activeRoom.disconnect();
    this.setState({ isConnected: false, isConnecting: false });

    if (!route) {
      return;
    }

    if (isAuthorized("user:patient")) {
      if (
        this.state.remoteTracks.length === 0 &&
        !this.state.participantDisconnected &&
        this.state.callDetails.provider_role !== "BEHAVIORAL_INTAKE" &&
        this.state.callDetails.provider_role !== "PRESCRIBE"
      ) {
        this.setStep("PROVIDER_MESSAGE_STEP");
      } else {
        this.setStep("RECONNECT");
      }

      return;
    }

    if (!isAuthorized("user:patient")) {
      this.props.history.push(this.props.authRedirectPath);
    }
  };

  redirectToMessageProviderDialog = () => {
    if (
      this.state.callDetails.provider_role !== "BEHAVIORAL_INTAKE" &&
      this.state.callDetails.provider_role !== "PRESCRIBE"
    ) {
      this.setStep("PROVIDER_MESSAGE_STEP");
    } else {
      this.props.history.push("/app/home");
    }
  };

  attachTracks = (tracks, videoRef, audioRef) => {
    tracks.forEach((track) => {
      if (track.kind === "data") {
        return;
      }

      let ref = track.kind === "video" ? videoRef : audioRef;

      if (track?.attach && typeof track.attach == "function") {
        track.attach(ref.current);
      } else {
        this.attachError(
          `TypeError: e.attach is not a function. track: ${JSON.stringify(track)} \n`,
        );
      }
    });
  };

  detachTracks = (tracks) => {
    tracks.forEach((track) => {
      track.stop();
    });
  };

  diffTimesInMinutes = (startTime, endTime) => {
    return moment.duration(startTime.diff(endTime)).asMinutes();
  };

  render() {
    if (!this.state.callDetails) {
      return <Spinner />;
    }

    return (
      <CallStepRouter
        activeRoom={this.state.activeRoom}
        allottedTime={this.state.allottedTime}
        attachLocalTracks={this.attachLocalTracks}
        attachRemoteTracks={this.attachRemoteTracks}
        callDetails={this.state.callDetails}
        endCall={this.endCall}
        isConnecting={this.state.isConnecting}
        localAudioRef={this.localAudioRef}
        localMedia={this.localMedia}
        localTracks={this.state.localTracks}
        localVideoRef={this.localVideoRef}
        otherParticipantName={this.state.otherParticipantName}
        participantDisconnected={this.state.participantDisconnected}
        redirectToMessageProviderDialog={this.redirectToMessageProviderDialog}
        remoteAudioRef={this.remoteAudioRef}
        remoteMedia={this.remoteMedia}
        remoteTracks={this.state.remoteTracks}
        remoteVideoPlaceholderRef={this.remoteVideoPlaceholderRef}
        remoteVideoRef={this.remoteVideoRef}
        setStep={this.setStep}
        startCall={this.startCall}
        startPreview={this.startPreview}
        startQuestions={this.startQuestions}
        startTime={this.state.startTime}
        step={this.state.step}
        warningTime={this.state.warningTime}
        workflow={this.state.workflow}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    authRedirectPath: state.auth.authRedirectPath,
    configs: state.configs,
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, { showAlertWithAction, showAlertWithCustomHTML })(
  VideoClient,
);
